import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  MenuItem,
  Select,
  Stack,
  TextField,
  Tooltip,
} from "@mui/material";
import FormatBoldIcon from "@mui/icons-material/FormatBold";
import FormatItalicIcon from "@mui/icons-material/FormatItalic";
import FormatUnderlinedIcon from "@mui/icons-material/FormatUnderlined";
import StrikethroughSIcon from "@mui/icons-material/StrikethroughS";
import FormatListBulletedIcon from "@mui/icons-material/FormatListBulleted";
import FormatListNumberedIcon from "@mui/icons-material/FormatListNumbered";
import FormatAlignLeftIcon from "@mui/icons-material/FormatAlignLeft";
import FormatAlignCenterIcon from "@mui/icons-material/FormatAlignCenter";
import FormatAlignRightIcon from "@mui/icons-material/FormatAlignRight";
import InsertLinkIcon from "@mui/icons-material/InsertLink";
import LinkOffIcon from "@mui/icons-material/LinkOff";
import FormatClearIcon from "@mui/icons-material/FormatClear";
import UndoIcon from "@mui/icons-material/Undo";
import RedoIcon from "@mui/icons-material/Redo";
import { EditorContent, useEditor, useEditorState } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import TextAlign from "@tiptap/extension-text-align";
import { TextStyleKit } from "@tiptap/extension-text-style";

const FONT_SIZES = ["12px", "14px", "16px", "18px", "24px", "32px"];

// Module-level so the array identity is stable across renders.
const EXTENSIONS = [
  StarterKit.configure({
    heading: false,
    code: false,
    codeBlock: false,
    horizontalRule: false,
    link: {
      openOnClick: false,
      HTMLAttributes: { target: "_blank", rel: "noopener noreferrer" },
    },
  }),
  TextAlign.configure({ types: ["paragraph"], defaultAlignment: "left" }),
  TextStyleKit.configure({
    fontFamily: false,
    lineHeight: false,
    backgroundColor: false,
  }),
];

const ToolbarButton = ({ title, onClick, active, disabled, children }) => (
  <Tooltip title={title}>
    <span>
      <IconButton
        size="small"
        disabled={disabled}
        onMouseDown={(e) => e.preventDefault()} // keep editor selection
        onClick={onClick}
        color={active ? "primary" : "default"}
        sx={{
          borderRadius: 1,
          bgcolor: active ? "action.selected" : undefined,
        }}
      >
        {children}
      </IconButton>
    </span>
  </Tooltip>
);

const RichTextEditor = ({
  value = "",
  onChange,
  onEditorReady,
  minHeight = 280,
}) => {
  const [linkOpen, setLinkOpen] = useState(false);
  const [linkUrl, setLinkUrl] = useState("");

  const editor = useEditor({
    extensions: EXTENSIONS,
    content: value,
    onUpdate: ({ editor }) => onChange?.(editor.getHTML()),
  });

  // Sync external changes (reset after send, template load) without echoing back.
  useEffect(() => {
    if (editor && value !== editor.getHTML()) {
      editor.commands.setContent(value || "", { emitUpdate: false });
    }
  }, [value, editor]);

  useEffect(() => {
    onEditorReady?.(editor);
  }, [editor, onEditorReady]);

  const s = useEditorState({
    editor,
    selector: ({ editor }) =>
      editor
        ? {
            bold: editor.isActive("bold"),
            italic: editor.isActive("italic"),
            underline: editor.isActive("underline"),
            strike: editor.isActive("strike"),
            bullet: editor.isActive("bulletList"),
            ordered: editor.isActive("orderedList"),
            left: editor.isActive({ textAlign: "left" }),
            center: editor.isActive({ textAlign: "center" }),
            right: editor.isActive({ textAlign: "right" }),
            link: editor.isActive("link"),
            fontSize: editor.getAttributes("textStyle").fontSize || "",
            color: editor.getAttributes("textStyle").color || "#000000",
            canUndo: editor.can().undo(),
            canRedo: editor.can().redo(),
          }
        : null,
  });

  if (!editor || !s) return null;

  const run = () => editor.chain().focus();

  const openLinkDialog = () => {
    setLinkUrl(editor.getAttributes("link").href || "");
    setLinkOpen(true);
  };

  const applyLink = () => {
    let href = linkUrl.trim();
    setLinkOpen(false);
    if (!href) {
      run().extendMarkRange("link").unsetLink().run();
      return;
    }
    if (!/^(https?:\/\/|mailto:)/i.test(href)) href = `https://${href}`;

    const { from, to } = editor.state.selection;
    if (from === to && !editor.isActive("link")) {
      run()
        .insertContent({
          type: "text",
          text: href,
          marks: [{ type: "link", attrs: { href } }],
        })
        .run();
    } else {
      run().extendMarkRange("link").setLink({ href }).run();
    }
  };

  return (
    <Box
      sx={{
        border: 1,
        borderColor: "divider",
        borderRadius: 1,
        overflow: "hidden",
        "&:focus-within": { borderColor: "primary.main" },
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        flexWrap="wrap"
        gap={0.25}
        sx={{
          p: 0.5,
          borderBottom: 1,
          borderColor: "divider",
          bgcolor: "grey.50",
        }}
      >
        <ToolbarButton
          title="Undo"
          disabled={!s.canUndo}
          onClick={() => run().undo().run()}
        >
          <UndoIcon fontSize="small" />
        </ToolbarButton>
        <ToolbarButton
          title="Redo"
          disabled={!s.canRedo}
          onClick={() => run().redo().run()}
        >
          <RedoIcon fontSize="small" />
        </ToolbarButton>
        <Divider flexItem orientation="vertical" sx={{ mx: 0.5 }} />

        <Select
          size="small"
          displayEmpty
          value={s.fontSize}
          onChange={(e) =>
            e.target.value
              ? run().setFontSize(e.target.value).run()
              : run().unsetFontSize().run()
          }
          sx={{ minWidth: 92, height: 30, fontSize: 13 }}
        >
          <MenuItem value="">Default</MenuItem>
          {FONT_SIZES.map((f) => (
            <MenuItem key={f} value={f}>
              {f}
            </MenuItem>
          ))}
        </Select>
        <Divider flexItem orientation="vertical" sx={{ mx: 0.5 }} />

        <ToolbarButton
          title="Bold"
          active={s.bold}
          onClick={() => run().toggleBold().run()}
        >
          <FormatBoldIcon fontSize="small" />
        </ToolbarButton>
        <ToolbarButton
          title="Italic"
          active={s.italic}
          onClick={() => run().toggleItalic().run()}
        >
          <FormatItalicIcon fontSize="small" />
        </ToolbarButton>
        <ToolbarButton
          title="Underline"
          active={s.underline}
          onClick={() => run().toggleUnderline().run()}
        >
          <FormatUnderlinedIcon fontSize="small" />
        </ToolbarButton>
        <ToolbarButton
          title="Strikethrough"
          active={s.strike}
          onClick={() => run().toggleStrike().run()}
        >
          <StrikethroughSIcon fontSize="small" />
        </ToolbarButton>
        <Tooltip title="Text color">
          <Box
            component="input"
            type="color"
            value={s.color}
            onChange={(e) => run().setColor(e.target.value).run()}
            sx={{
              width: 26,
              height: 26,
              p: 0,
              border: 0,
              bgcolor: "transparent",
              cursor: "pointer",
            }}
          />
        </Tooltip>
        <Divider flexItem orientation="vertical" sx={{ mx: 0.5 }} />

        <ToolbarButton
          title="Align left"
          active={s.left}
          onClick={() => run().setTextAlign("left").run()}
        >
          <FormatAlignLeftIcon fontSize="small" />
        </ToolbarButton>
        <ToolbarButton
          title="Align center"
          active={s.center}
          onClick={() => run().setTextAlign("center").run()}
        >
          <FormatAlignCenterIcon fontSize="small" />
        </ToolbarButton>
        <ToolbarButton
          title="Align right"
          active={s.right}
          onClick={() => run().setTextAlign("right").run()}
        >
          <FormatAlignRightIcon fontSize="small" />
        </ToolbarButton>
        <Divider flexItem orientation="vertical" sx={{ mx: 0.5 }} />

        <ToolbarButton
          title="Bulleted list"
          active={s.bullet}
          onClick={() => run().toggleBulletList().run()}
        >
          <FormatListBulletedIcon fontSize="small" />
        </ToolbarButton>
        <ToolbarButton
          title="Numbered list"
          active={s.ordered}
          onClick={() => run().toggleOrderedList().run()}
        >
          <FormatListNumberedIcon fontSize="small" />
        </ToolbarButton>
        <Divider flexItem orientation="vertical" sx={{ mx: 0.5 }} />

        <ToolbarButton
          title="Insert / edit link"
          active={s.link}
          onClick={openLinkDialog}
        >
          <InsertLinkIcon fontSize="small" />
        </ToolbarButton>
        <ToolbarButton
          title="Remove link"
          disabled={!s.link}
          onClick={() => run().extendMarkRange("link").unsetLink().run()}
        >
          <LinkOffIcon fontSize="small" />
        </ToolbarButton>
        <ToolbarButton
          title="Clear formatting"
          onClick={() => run().unsetAllMarks().clearNodes().run()}
        >
          <FormatClearIcon fontSize="small" />
        </ToolbarButton>
      </Stack>

      <Box
        sx={{
          "& .tiptap": { minHeight, p: 1.5, outline: "none", fontSize: 14 },
          "& .tiptap p": { m: 0, mb: "0.75em" },
          "& .tiptap ul, & .tiptap ol": { pl: 3, my: 1 },
          "& .tiptap li p": { mb: 0 },
          "& .tiptap a": { color: "primary.main", textDecoration: "underline" },
        }}
      >
        <EditorContent editor={editor} />
      </Box>

      <Dialog
        open={linkOpen}
        onClose={() => setLinkOpen(false)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Link</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            margin="dense"
            label="URL"
            placeholder="https://example.com"
            value={linkUrl}
            onChange={(e) => setLinkUrl(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && applyLink()}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLinkOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={applyLink}>
            Apply
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default RichTextEditor;
